// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo } from 'react';

import {
  type StrongOmit,
  type CreateOrUpdateLabelRequest,
  type Label,
  type LabelId,
  type LabelTypeId,
  useEvolveDelete,
  useEvolvePost,
  useEvolvePut,
} from '@evolvemep/api-ts';

import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

export type LabelsContextType = {
  labels: Label[];
  labelMap: Record<LabelId, Label>;
  labelTypeId: LabelTypeId;
  loading: boolean;
  createLabel: (body: StrongOmit<CreateOrUpdateLabelRequest, 'labelTypeId'>) => Promise<Label>;
  updateLabel: (labelId: LabelId, body: StrongOmit<CreateOrUpdateLabelRequest, 'labelTypeId'>) => Promise<Label>;
  deleteLabel: (labelId: LabelId) => Promise<void>;
  mutationRunning: boolean;
};

export const useLabelsContextValue = (labelTypeId: LabelTypeId) => {
  const {
    data: labels = [],
    loading,
    updateStoredValue: setLabels,
  } = useEvolveGetWithLocalStorage('shop/label', {
    defaultOpts: {
      defaultConfig: { params: { labelTypeIds: [labelTypeId] } },
    },
    appendKey: labelTypeId,
    delay: false,
  });

  const labelMap = useMemo(() => {
    const map: Record<LabelId, Label> = {};
    labels.forEach((label) => {
      map[label.labelId] = label;
    });
    return map;
  }, [labels]);

  const { apiCall: createLabelApiCall, loading: creatingLabel } = useEvolvePost('shop/label');
  const createLabel = useCallback(
    async (newLabel: StrongOmit<CreateOrUpdateLabelRequest, 'labelTypeId'>) => {
      const createdLabel = await createLabelApiCall({
        ...newLabel,
        labelTypeId,
      });
      setLabels((prevLabels = []) => [...prevLabels, createdLabel].toSorted((a, b) => a.name.localeCompare(b.name)));

      return createdLabel;
    },
    [createLabelApiCall, setLabels, labelTypeId],
  );

  const { apiCall: updateLabelApiCall, loading: updatingLabel } = useEvolvePut('shop/label/:labelId');
  const updateLabel = useCallback(
    async (labelId: LabelId, updatedLabel: StrongOmit<CreateOrUpdateLabelRequest, 'labelTypeId'>) => {
      const label = await updateLabelApiCall(
        {
          ...updatedLabel,
          labelTypeId,
        },
        { url: `shop/label/${labelId}` },
      );
      setLabels((prevLabels = []) => {
        const index = prevLabels.findIndex((prevLabel) => prevLabel.labelId === labelId);
        if (index === -1) return prevLabels;
        const newLabels = [...prevLabels];
        newLabels[index] = label;
        return newLabels.toSorted((a, b) => a.name.localeCompare(b.name));
      });

      return label;
    },
    [updateLabelApiCall, setLabels, labelTypeId],
  );

  const { apiCall: deleteLabelApiCall, loading: deletingLabel } = useEvolveDelete('shop/label/:labelId');
  const deleteLabel = useCallback(
    async (labelId: LabelId) => {
      await deleteLabelApiCall({ url: `shop/label/${labelId}` });
      setLabels((prevLabels = []) => prevLabels.filter((prevLabel) => prevLabel.labelId !== labelId));
    },
    [deleteLabelApiCall, setLabels],
  );

  const mutationRunning = creatingLabel || updatingLabel || deletingLabel;

  return useMemo(
    () => ({
      labels,
      labelMap,
      labelTypeId,
      loading,
      createLabel,
      updateLabel,
      deleteLabel,
      mutationRunning,
    }),
    [labels, labelMap, labelTypeId, loading, createLabel, updateLabel, deleteLabel, mutationRunning],
  );
};
